import { translate } from "../localization";
export const ELEMENT_OPTIMIZATION_MODE_OPTIONS = [
  { value: "automatic", get label() { return translate("Automatic"); } },
  { value: "automatic_large", get label() { return translate("Automatic Large"); } },
  { value: "none", get label() { return translate("None"); } },
  { value: "custom", get label() { return translate("Custom"); } },
];

export const ELEMENT_OPTIMIZATION_METHOD_OPTIONS = [
  { value: "remesh", get label() { return translate("Remesh"); } },
  { value: "decimate", get label() { return translate("Decimate"); } },
];

const DEFAULT_ELEMENT_OPTIMIZATION_FORM = Object.freeze({
  mode: "automatic",
  method: "remesh",
  targetFaces: "20000",
});

export function createDefaultElementOptimizationForm() {
  return { ...DEFAULT_ELEMENT_OPTIMIZATION_FORM };
}

export function createElementOptimizationFormFromPayload(optimization = {}) {
  const mode = normalizeOptimizationMode(optimization.mode);

  if (mode !== "custom") {
    return {
      ...DEFAULT_ELEMENT_OPTIMIZATION_FORM,
      mode,
    };
  }

  return {
    mode,
    method: normalizeOptimizationMethod(optimization.method),
    targetFaces: String(
      parseOptimizationTargetFaces(optimization.targetFaces) ||
        DEFAULT_ELEMENT_OPTIMIZATION_FORM.targetFaces
    ),
  };
}

export function isCustomElementOptimizationMode(mode) {
  return String(mode || "").trim().toLowerCase() === "custom";
}

export function parseOptimizationTargetFaces(value) {
  const parsed = Number.parseInt(String(value || "").trim(), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function isValidElementOptimizationForm(optimization) {
  if (!isCustomElementOptimizationMode(optimization?.mode)) {
    return true;
  }

  return (
    isSupportedOptimizationMethod(optimization?.method) &&
    parseOptimizationTargetFaces(optimization?.targetFaces) !== null
  );
}

export function buildElementOptimizationPayload(optimization = {}) {
  const mode = normalizeOptimizationMode(optimization.mode);

  if (mode !== "custom") {
    return { mode };
  }

  return {
    mode,
    method: normalizeOptimizationMethod(optimization.method),
    targetFaces: parseOptimizationTargetFaces(optimization.targetFaces) || 20000,
  };
}

export function getElementOptimizationModeHelperText(mode) {
  switch (normalizeOptimizationMode(mode)) {
    case "automatic_large":
      return translate("Use the larger automatic optimization profile when preprocessing is needed.");
    case "none":
      return translate("Skip preprocessing and send the source file directly to the build pipeline.");
    case "custom":
      return translate("Force preprocessing with the method and target faces below.");
    case "automatic":
    default:
      return translate("Use the default preprocessing heuristics and automatic optimization profile.");
  }
}

function normalizeOptimizationMode(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (
    normalized === "automatic" ||
    normalized === "automatic_large" ||
    normalized === "none" ||
    normalized === "custom"
  ) {
    return normalized;
  }

  return DEFAULT_ELEMENT_OPTIMIZATION_FORM.mode;
}

function normalizeOptimizationMethod(value) {
  return isSupportedOptimizationMethod(value)
    ? String(value).trim().toLowerCase()
    : DEFAULT_ELEMENT_OPTIMIZATION_FORM.method;
}

function isSupportedOptimizationMethod(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "remesh" || normalized === "decimate";
}
